
import Vue from 'vue';
import axios from 'axios';
import AuditHistoryTimePicker from '../components/AuditHistoryTimePicker.vue';
import AuditHistoryAddress from '../components/AuditHistoryAddress.vue';
import AuditHistorySnackbars from '../components/AuditHistorySnackbars.vue';
import AuditHistoryUsers from '../components/AuditHistoryUsers.vue';
import EventService from '@/services/EventService';
import {mapState} from 'vuex';
import store from '../store';
import {saveAs} from 'file-saver';

export type VForm = Vue & {validate: () => boolean};

type HistoryData = {
  data: {
    schedule: Schedule;
    taskList: Array<Task>;
    memberList: Array<Member>;
    auditorList: Array<Auditor>;
    audit: AuditEvent;
  };
};

type Schedule = {
  date: string;
  start: string;
  end: string;
  place: string;
};

type Task = {
  id: string;
  summary: string;
  detail: string;
  deadline: string;
  goal: string;
  menu: boolean;
};

type Member = {
  name: string;
  position: string;
  id: string;
};

type Auditor = {
  name: string;
  organization: string;
  email: string;
  phone: string;
  id: string;
};

type AuditEvent = {
  summary: string;
  detail: string;
  file?: Array<File>;
};

interface Snackbar {
  messages: Array<string>;
  color: string;
  timeout: number;
  hock?: () => void;
}

type Item = {
  text: string;
  to?: string;
  disabled: boolean;
};

type Backup = {
  audit: AuditEvent;
  auditorList: Array<Auditor>;
  memberList: Array<Member>;
  taskList: Array<Task>;
  schedule: Schedule;
};

type Data = {
  users: string[];
  isBackup: boolean;
  deletable: boolean;
  editable: boolean;
  backup?: Backup;
  disabledBtn: boolean;
  crumbs: Array<Item>;
  audit: AuditEvent;
  auditorList: Array<Auditor>;
  memberList: Array<Member>;
  taskList: Array<Task>;
  schedule: Schedule;
  menu: boolean;
  dateRule: Array<FormRule>;
  timeFormatRule: Array<FormRule>;
  placeRule: Array<FormRule>;
  memberNameRule: Array<FormRule>;
  auditorNameRule: Array<FormRule>;
  summaryRule: Array<FormRule>;
  snackbar: Snackbar;
  validated: boolean;
};

type RestData = {
  audit: AuditEvent;
  auditorList: Array<Auditor>;
  memberList: Array<Member>;
  schedule: Schedule;
  taskList: Array<Task>;
};

type FormRule =
  | ((val?: string) => boolean | string)
  | ((val: string) => boolean | string);

const baseURL = EventService.ProtoURL();
const apiClient = axios.create({
  baseURL: baseURL,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

apiClient.interceptors.request.use(config => {
  const companyID = store.state.current;
  if (companyID !== '') {
    config.headers['x-company-id'] = companyID;
  }
  return config;
});

const deleteHistory = async (historyID: string, token: string) => {
  await apiClient.delete(`/histories/id/${historyID}`, {
    headers: {Authorization: `Bearer ${token}`},
  });
};

const putHistory = async (
  historyID: string,
  token: string,
  param: HistoryData,
  fileform?: FormData
) => {
  const waiter = [];
  const {data} = await apiClient.put(`/histories/id/${historyID}`, param, {
    headers: {Authorization: `Bearer ${token}`},
  });
  console.log(data);

  if (fileform) {
    const fileResponse = apiClient.post(
      `/histories/files/${historyID}`,
      fileform,
      {
        headers: {
          'content-type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      }
    );
    waiter.push(fileResponse);
  }

  await Promise.all(waiter);
};

const postHistory = async (
  token: string,
  param: HistoryData,
  fileform?: FormData
) => {
  const waiter = [];
  const {data} = await apiClient.post('/histories/', param, {
    headers: {Authorization: `Bearer ${token}`},
  });

  if (fileform) {
    const fileResponse = apiClient.post(
      `/histories/files/${data.id}`,
      fileform,
      {
        headers: {
          'content-type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      }
    );
    waiter.push(fileResponse);
  }

  await Promise.all(waiter);
};

type Res = {
  data: RestData;
};

type URes = {
  users: string[];
};

export default Vue.extend({
  name: 'audit-history',
  computed: {...mapState(['user'])},
  components: {
    AuditHistoryTimePicker,
    AuditHistoryAddress,
    AuditHistorySnackbars,
    AuditHistoryUsers,
  },
  async mounted() {
    if (localStorage.getItem(`backup-history-${this.historyID}`)) {
      this.isBackup = true;
    }
    if (this.user) {
      const token = await this.user.getIdToken();
      const ures = await apiClient.get<URes>(`/histories/users/`, {
        headers: {
          'content-type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      });
      this.users = ures.data.users;
      if (this.historyID === 'new') {
        (this.$refs.history as VForm).validate();
        this.editable = true;
      } else {
        try {
          const res = await apiClient.get<Res>(
            `/histories/id/${this.historyID}`,
            {
              headers: {
                'content-type': 'multipart/form-data',
                Authorization: `Bearer ${token}`,
              },
            }
          );
          const rest = res.data.data;
          this.audit = rest.audit;
          this.schedule = rest.schedule;
          this.auditorList = rest.auditorList;
          this.memberList = rest.memberList;
          this.taskList = rest.taskList;
          this.crumbs.push({
            text: '監査履歴',
            to: '/audithistoryData',
            disabled: false,
          });
          this.crumbs.push({
            text: this.audit.summary,
            disabled: true,
          });
        } catch (e) {
          console.log(e);
        }
      }
    }
  },

  updated() {
    (this.$refs.history as VForm).validate();
  },
  props: {
    historyID: {
      type: String,
      default: 'new',
    },
  },
  data(): Data {
    return initState();
  },
  methods: {
    async wdownload() {
      if (this.user) {
        const token = await this.user.getIdToken();
        const {data} = await apiClient.get(
          `/histories/word/${this.historyID}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            responseType: 'blob',
          }
        );
        saveAs(new Blob([data]), 'report.docx');
      }
    },
    async download() {
      if (this.user) {
        const token = await this.user.getIdToken();
        const {data} = await apiClient.get(
          `/histories/file/${this.historyID}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            responseType: 'blob',
          }
        );
        saveAs(new Blob([data]), 'report.pdf');
      }
    },
    save() {
      this.backup = {
        audit: this.audit,
        auditorList: this.auditorList,
        memberList: this.memberList,
        taskList: this.taskList,
        schedule: this.schedule,
      };
      this.isBackup = true;
      const json = JSON.stringify(this.backup);
      localStorage.setItem(`backup-history-${this.historyID}`, json);
    },
    async deletehistory() {
      console.log('delete');
      this.disabledBtn = true;
      try {
        if (this.user) {
          const token = await this.user.getIdToken();
          await deleteHistory(this.historyID, token);
          this.snackbar.messages = ['正常に削除されました'];
          this.snackbar.color = 'success';
          this.snackbar.timeout = -1;
          this.snackbar.hock = () => {
            this.$router.push({name: 'audithistory-data'});
          };
        }
      } catch (e) {
        this.snackbar.messages = ['error'];
        this.snackbar.color = 'error';
        this.disabledBtn = false;
      }
    },
    restore() {
      const val = localStorage.getItem(`backup-history-${this.historyID}`);
      if (val) {
        this.backup = JSON.parse(val);
      }
      if (this.backup) {
        this.audit = this.backup.audit;
        this.auditorList = this.backup.auditorList;
        this.memberList = this.backup.memberList;
        this.taskList = this.backup.taskList;
        this.schedule = this.backup.schedule;
        this.audit.file = undefined;
      }
    },
    add() {
      this.memberList.push({name: '', position: '', id: genRand(8)});
    },

    remove(i: number) {
      this.memberList.splice(i, 1);
    },

    aAdd() {
      this.auditorList.push({
        name: '',
        organization: '',
        email: '',
        phone: '',
        id: genRand(8),
      });
    },

    aRemove(i: number) {
      this.auditorList.splice(i, 1);
    },

    tAdd() {
      this.taskList.push({
        summary: '',
        detail: '',
        deadline: '',
        goal: '',
        menu: false,
        id: genRand(8),
      });
    },

    tRemove(i: number) {
      this.taskList.splice(i, 1);
    },

    createFileForm(): FormData | undefined {
      if (this.audit.file && this.audit.file.length > 0) {
        const form = new FormData();
        this.audit.file.forEach(v => form.append(v.name, v));
        return form;
      } else {
        return undefined;
      }
    },
    async update() {
      this.disabledBtn = true;
      if (!this.validated) {
        this.snackbar.messages = ['必要項目が入力されてません'];
        this.snackbar.color = 'error';
        this.disabledBtn = false;
        return;
      }
      const data = {
        schedule: this.schedule,
        taskList: this.taskList,
        memberList: this.memberList,
        auditorList: this.auditorList,
        audit: this.audit,
      };

      localStorage.removeItem(`backup-history-${this.historyID}`);
      this.isBackup = false;

      const form = this.createFileForm();
      try {
        if (this.user) {
          const token = await this.user.getIdToken();
          await putHistory(this.historyID, token, {data: data}, form);
          this.snackbar.messages = ['正常に保存されました'];
          this.snackbar.color = 'success';
        }
      } catch (e) {
        this.snackbar.messages = ['error'];
        this.snackbar.color = 'error';
      } finally {
        this.disabledBtn = false;
      }
      return;
    },
    async store() {
      this.disabledBtn = true;
      if (!this.validated) {
        this.snackbar.messages = ['必要項目が入力されてません'];
        this.snackbar.color = 'error';
        this.disabledBtn = false;
        return;
      }
      const data = {
        schedule: this.schedule,
        taskList: this.taskList,
        memberList: this.memberList,
        auditorList: this.auditorList,
        audit: this.audit,
      };

      localStorage.removeItem(`backup-history-${this.historyID}`);
      this.isBackup = false;
      const form = this.createFileForm();

      try {
        if (this.user) {
          const token = await this.user.getIdToken();
          await postHistory(token, {data: data}, form);
          this.snackbar.messages = ['正常に保存されました'];
          this.snackbar.color = 'success';
        }
      } catch (e) {
        this.snackbar.messages = ['error'];
        this.snackbar.color = 'error';
      } finally {
        this.disabledBtn = false;
      }
    },
  },
});

const genRand = (l: number): string => {
  // 生成する文字列に含める文字セット
  const c = 'abcdefghijklmnopqrstuvwxyz0123456789';

  var cl = c.length;
  let r = '';
  for (var i = 0; i < l; i++) {
    r += c[Math.floor(Math.random() * cl)];
  }
  return r;
};

const initState = (): Data => {
  return {
    users: [],
    isBackup: false,
    deletable: false,
    editable: false,
    backup: undefined,
    disabledBtn: false,
    crumbs: [],
    validated: false,
    snackbar: {
      messages: [''],
      color: 'success',
      timeout: 5000,
    },
    audit: {
      summary: '',
      detail: '',
      file: undefined,
    },
    auditorList: [
      {
        name: '',
        organization: '',
        email: '',
        phone: '',
        id: genRand(8),
      },
    ],
    memberList: [
      {
        name: '',
        position: '',
        id: genRand(8),
      },
    ],
    taskList: [],
    schedule: {
      date: '',
      start: '',
      end: '',
      place: '',
    },
    menu: false,
    dateRule: [
      (val?: string) => !!val || '右のカレンダーから日付を選択してください',
    ],
    timeFormatRule: [
      (val?: string) => !!val || '時間を入力してください',
      (val: string) =>
        /^[0-9]{2}:[0-9]{2}/.test(val) ||
        'HH/MM (例 09:15, 13:45) のように入力してください',
    ],
    placeRule: [(val?: string) => !!val || '開催場所を入力してください'],
    memberNameRule: [
      (val?: string) => !!val || '参加者の名前を入力してください',
    ],
    auditorNameRule: [
      (val?: string) => !!val || '教師/監査官の名前を入力してください',
    ],
    summaryRule: [
      (val?: string) =>
        !!val ||
        '受講/監査内容の要約を入力して下さい 最初の約40文字がリストのタイトルとして表示されます 40文字以降は詳細を確認ください',
    ],
  };
};
